/**
 * Preset settings shape — the pure functions used when saving and loading presets.
 *
 * `preset.settings` and `live` are the SAME shape ({diffusion, motion, control, ip, filter, inpaint, objects}), sparse on both sides, so loading is `migrateSettings` and nothing more. These live here rather than in store/playback/thunks.js because they are pure functions over that shape, and importing them must not pull in the store graph (the migration and its tests need them without RTK Query).
 */
import { conformModulators } from '~/lib/automation/model'
import { isMorphEnvelope } from '~/schemas/morph'
import { DROP, quantize, settleSourceSettings, settleValue } from '~/lib/params'
import { migrateSettings } from '~/lib/migrations'
import { morphToPlain } from '~/lib/morph'
import display_object_schema from '~/schemas/display_object_schema'
import { EFFECT_SCHEMAS, WIRE_KEYS } from '~/schemas/effect_schemas'

export { EFFECT_SCHEMAS, WIRE_KEYS }

/**
 * Absence means AT INITIAL, in every carrier and for every param, with no exemptions. NOTHING expands — a whole-record filler would make `live` a shape no write can produce, since a write that returns a param to its initial DELETES the key. Each read resolves its own key through `resolve` (`~/lib/params`), and `prune` below is only the write-side half of the same rule.
 *
 * ⚠️ `resolve` treats an explicit `null` as absent (`== null`), while `prune` compares with `!==` so a stored `null` is kept rather than silently pruned. That asymmetry is deliberate: legacy presets persist explicit nulls for `opacity`/`flipX`/`locked`/`mask`, and handing one to a consumer is a live bug — `setOpacityVars` computes `alpha = null / 100 = 0` and fades a fully-visible object to black for a whole morph. Resolving null on the way OUT fixes every reader at once; pruning it on the way IN would rewrite stored data the migration chain owns (`dropNullKeys`).
 */

/** THE prune — `stripSettings` and `conform` both run it, differing only by options. See [presets.md](../../context/presets.md) § The Write Rule. */
export const prune = (params, props, { transform, dropUndeclared = false } = {}) => {
	const out = {}
	for (const key in params) {
		const prop = props?.[key]
		// ⚠️ An undeclared key is KEPT unless asked otherwise — no contract entry means no initial to compare. `conform` drops instead: an effect's params are a closed vocabulary on the wire, where a stray `_v` fails a strict Pydantic model.
		if (!prop) {
			if (!dropUndeclared) out[key] = params[key]
			continue
		}
		// ⚠️ A MORPH ENVELOPE IS SETTLED TO `b` FIRST — an envelope describes a fade in flight and a preset is a resting state, so one may never be stored.
		const settled = isMorphEnvelope(params[key]) ? morphToPlain(params[key]) : params[key]
		// The transform (`conform`'s coerce+clamp) runs on the quantized value and may itself return the initial, so the rule judges what it produced.
		const value = settleValue(prop, transform ? transform(key, quantize(prop, settled), prop) : settled)
		if (value !== DROP) out[key] = value
	}
	return out
}

/** Prune one section and say whether it survives — an emptied effect goes with its last param, so at-initial has ONE spelling at every level. */
export const pruneSection = (params, props, opts) => {
	const out = prune(params, props, opts)
	return Object.keys(out).length ? out : null
}

/** The `properties` map for an object type, or undefined for an unknown type. */
export const objectProps = type => display_object_schema.definitions[type]?.properties

/**
 * Persist only the keys that differ from their schema `initial`. Objects stay as-is (already sparse by design).
 *
 * ⚠️ It iterates only the effects the input HAS — materializing an absent one would resurrect what the user removed.
 *
 * ⚠️ AT-INITIAL HAS ONE SPELLING, ABSENCE, at both levels — the rule `conform` applies at the cache boundary, which every save also passes through. Nothing consumes present-vs-absent today, and two spellings drift.
 */
export function stripSettings(settings) {
	if (!settings) return settings

	const out = { ...settings }
	for (const name of Object.keys(EFFECT_SCHEMAS)) {
		if (settings[name] === undefined) continue
		const section = pruneSection(settings[name], EFFECT_SCHEMAS[name].properties)
		if (section) out[name] = section
		else delete out[name]
	}

	// A group's size is `scale` over its children's bbox — derived, so persisting it stores the same fact twice and the copy goes stale the moment a child moves.
	if (settings.objects) {
		let objects = settings.objects
		for (const id in objects) {
			const o = objects[id]
			if (o?.type !== 'group' || (o.width === undefined && o.height === undefined)) continue
			if (objects === settings.objects) objects = { ...objects }
			// eslint-disable-next-line no-unused-vars
			const { width: _w, height: _h, ...rest } = o
			objects[id] = rest
		}
		if (objects !== settings.objects) out.objects = objects
	}

	if (settings.modulators) {
		const modulators = conformModulators(settings.modulators, out.objects)
		if (modulators) out.modulators = modulators
		else delete out.modulators
	}

	if (settings.source_settings) {
		const sourceSettings = settleSourceSettings(settings.source_settings, out.objects)
		if (sourceSettings) out.source_settings = sourceSettings
		else delete out.source_settings
	}

	return out
}

// Strip advanced controls from preset settings before saving. advanced values are schema-free and server-version-specific — unsafe to persist.
export function stripAdvancedFromSettings(settings) {
	if (!settings?.diffusion) return settings
	const { advanced: _adv, ...diffusionRest } = settings.diffusion
	return { ...settings, diffusion: diffusionRest }
}

/** A stored preset's settings as a load applies them: legacy `playback` dropped, advanced stripped, migrated. */
export function loadableSettings(settings) {
	const { playback: _playback, ...rest } = settings ?? {}
	return migrateSettings(stripAdvancedFromSettings(rest))
}
